import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { STALE } from "@/lib/api/query-client";
import { keys } from "@/lib/api/query-keys";
import { chatApi, chatFilesApi } from "./chat.api";

/**
 * Query definitions for chat.
 *
 * `queryOptions()` factories rather than bespoke hooks, for the reason the standard gives: the
 * key and the fetcher travel together, so the rail that reads a list, the launcher that reads
 * the same list and the mutation that invalidates it all name one thing. A hand-written
 * `useQuery({ queryKey: [...] })` at a call site is where a key that nothing invalidates comes
 * from.
 *
 * NOTHING HERE IS SCOPED TO A COMPANY. A conversation belongs to a USER and the tables carry no
 * `legal_entity_id`; putting the active company in the key would fragment one identical answer
 * across every company the person can switch to and imply a scoping rule the server does not
 * have. `query-keys.ts` says the same thing above `keys.chat`.
 */

/** How many conversations the rail asks for. Above this the rail is a search problem, not a list. */
const RAIL_PAGE_SIZE = 50;

/** §14b caps `limit` at 100; 50 is one screen of history plus room to scroll into. */
export const MESSAGE_PAGE_SIZE = 50;

/**
 * The rail's one page.
 *
 * `q` IS DELIBERATELY NOT SENT. The rail is a single 50-row page and `ChatList` filters what it
 * holds — Turkish-folded, over the title, the preview and its author ("IT DOES NOT FETCH", and
 * search "is the one thing it does compute"). Sending the term as well filtered the list TWICE,
 * by two different rules: the server matches a member's display name, username or e-mail, so a
 * DM found by e-mail came back from the server and was then dropped by the client, and a term
 * that matched nothing on the server emptied `entries` — which takes `ChatList`'s no-data branch
 * and tells a searching user "Henüz konuşma yok" instead of showing a no-results state.
 *
 * The cost is that search cannot reach past the first 50 conversations. That is the rail's own
 * limit either way (`RAIL_PAGE_SIZE`), and `GET /chat/messages/search` is the endpoint for
 * finding something deeper than the rail holds.
 */
export function chatConversationsQuery(
  filters: { includeArchived: boolean },
  options?: { enabled?: boolean },
) {
  const params = {
    limit: RAIL_PAGE_SIZE,
    ...(filters.includeArchived ? { includeArchived: "1" } : {}),
  };

  return queryOptions({
    queryKey: keys.chat.conversations(params),
    queryFn: () => chatApi.conversations(params),
    // A rail is a work queue: it changes while you are looking at it, and a realtime frame
    // invalidates it anyway. `live` keeps a tab switch from refetching forty rows for nothing.
    staleTime: STALE.live,
    enabled: options?.enabled ?? true,
  });
}

/**
 * BİR SOHBETİN DETAYI VE MESAJLARI ÖNCEKİ SOHBETİN VERİSİNİ DEVRALMAZ.
 *
 * `query-client.ts` bütün sorgulara `placeholderData: (previous) => previous` veriyor — tablo
 * sayfalanırken satırlar boşalmasın diye. Bir konuşma için bu yanlış içerik demek: sayfa ya da
 * yuva tek bir `useChatThread` örneğiyle A'dan B'ye geçiyor ve B'nin sorgusu, B'nin cevabı
 * gelene kadar A'nın detayını ve mesajlarını döndürüyordu. Ekranda B'nin yerinde A'nın başlığı
 * ve mesajları duruyordu, «Yeni mesajlar» çizgisi A'nın sayısıyla çiziliyordu ve okundu imleci
 * `POST /chat/conversations/B/read` isteğini A'nın mesaj kimliğiyle gönderiyordu.
 *
 * Açık bir `undefined` varsayılanı kapatan tek yol (rehberin `personQuery` kalıbı): query-core
 * seçeneği yalnız tam olarak `undefined` değilken uyguluyor, ve `exactOptionalPropertyTypes`
 * altında tipin `undefined` taşımaması yüzünden değer `as never` ile veriliyor.
 *
 * Kapalıyken geçişte `data` gerçekten yok: yüzey iskeleti çiziyor. `useChatThread` ayrıca
 * `isPlaceholderData` iken ne imleci oynatıyor ne de çizgiyi sabitliyor — bir gün biri bu satırı
 * silerse ikinci kilit orada.
 */
export function chatConversationQuery(id: string | null, options?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: keys.chat.conversation(id ?? ""),
    queryFn: () => chatApi.conversation(id as string),
    // The full roster is what the mention list and the author names are built from; it changes
    // only when somebody joins or leaves, and a `chat.conversation` frame invalidates it then.
    staleTime: STALE.record,
    enabled: (options?.enabled ?? true) && id !== null && id !== "",
    placeholderData: undefined as never,
  });
}

/**
 * The thread, walked backwards through the cursor.
 *
 * THE CURSOR IS NEVER IN THE KEY. It is `pageParam`, which is what lets "load older" append a
 * page to the entry already in the cache instead of creating a second entry that holds the same
 * conversation at a different depth. `keys.chat.messages` says so in its own comment: the
 * filters part carries the page size, never the cursor.
 */
export function chatMessagesQuery(id: string | null, options?: { enabled?: boolean }) {
  return infiniteQueryOptions({
    queryKey: keys.chat.messages(id ?? "", { limit: MESSAGE_PAGE_SIZE }),
    queryFn: ({ pageParam }) =>
      chatApi.messages(id as string, {
        limit: MESSAGE_PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    // Zero, not `live`: an open thread is refetched by its realtime frames, and a stale window
    // would leave a message the user was just told about invisible for fifteen seconds.
    staleTime: 0,
    enabled: (options?.enabled ?? true) && id !== null && id !== "",
    placeholderData: undefined as never,
  });
}

/** The shortest term `GET /chat/messages/search` accepts (§14b: one letter is a 422). */
export const MESSAGE_SEARCH_MIN_CHARS = 2;

/**
 * MESAJ GEÇMİŞİNDE ARAMA (H6, 20 Ekim 2026) — rayın arama kutusunun «Mesajlar» bölümü.
 *
 * Ray yüklü 50 konuşmayı kendisi süzüyor; bir MESAJ yalnız sunucuda. Terim iki harfe ulaşınca
 * (sunucunun alt sınırı) ve kişi yazmayı bıraktığında (çağıran gecikmeli değeri veriyor) bir kez
 * soruluyor. Anahtar terimi taşıyor: «irs»in cevabı «irsaliye»nin altında görünmez — kit de
 * `remoteResults.query` ile aynı şeyi ayrıca denetliyor.
 *
 * `STALE.live`: arama bir an önceki hâlin fotoğrafı; aynı terim kısa sürede yeniden sorulursa
 * önbellek yeter, yeni mesaj gelince zaten `chat.message` çerçevesi listeyi tazeliyor.
 */
export function chatMessageSearchQuery(term: string, options?: { enabled?: boolean }) {
  const q = term.trim();
  return queryOptions({
    queryKey: keys.chat.search({ q }),
    queryFn: () => chatApi.searchMessages({ q, limit: 20 }),
    staleTime: STALE.live,
    enabled: (options?.enabled ?? true) && q.length >= MESSAGE_SEARCH_MIN_CHARS,
    // Bir önceki terimin sonuçları yeni terimin altında «eşleşme» gibi durmasın.
    placeholderData: undefined as never,
  });
}

/**
 * The badge number — `{ total, byConversation[] }`, not the list.
 *
 * §14b is explicit that the counter comes from here rather than from summing the rail, and the
 * launcher depends on it: the bubble must show a truthful total on a screen that has never
 * opened the conversation list at all.
 */
export function chatUnreadCountQuery(options?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: keys.chat.unreadCount(),
    queryFn: () => chatApi.unreadCount(),
    staleTime: STALE.live,
    enabled: options?.enabled ?? true,
  });
}

/**
 * The upload-slot catalogue — the size, count and mime rules the composer enforces.
 *
 * NOT IN `keys.chat`: `/files/purposes` belongs to the files module and is shared by notes and
 * the directory, so it is keyed under its own prefix rather than under a key `invalidates.chat`
 * would sweep. A chat write does not change what the slot accepts. Adding a shared
 * `keys.files.purposes(entityType)` is requested in this task's report rather than made here.
 *
 * `STALE.reference`: a slot definition changes when the server is redeployed, not while somebody
 * is typing.
 */
const CHAT_ATTACHMENT_PURPOSES_KEY = ["files", "purposes", "chat.message"] as const;

export function chatAttachmentPurposesQuery(options?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: CHAT_ATTACHMENT_PURPOSES_KEY,
    queryFn: () => chatFilesApi.purposes(),
    staleTime: STALE.reference,
    enabled: options?.enabled ?? true,
  });
}

/** The person picker. Disabled below two characters — a blank `q` asks for the whole company. */
export function chatPeopleQuery(term: string, options?: { enabled?: boolean }) {
  const params = { q: term, limit: 20, scope: "chat-picker" };
  return queryOptions({
    queryKey: keys.directory.people(params),
    queryFn: () => chatApi.people({ q: term, limit: 20 }),
    staleTime: STALE.record,
    /**
     * BOŞ TERİMDE DE ÇALIŞIYOR, iki harf beklemiyor.
     *
     * Eskiden alan açıldığında boş duruyor ve «Aramak için en az iki harf yazın» diyordu.
     * Küçük bir şirkette bu, bilinen on beş kişiyi yazarak aramak demekti — ve kimin kayıtlı
     * olduğunu bilmeyen biri hiç başlayamıyordu. Sunucu zaten `limit: 20` ile sınırlı bir
     * sayfa dönüyor, yani boş terim "herkesi getir" değil, "ilk yirmisini getir".
     */
    enabled: options?.enabled ?? true,
  });
}
